import {
	type InfiniteData,
	type QueryClient,
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { apiClient, withAuthRetry } from '@/api';

// Types
export type PaginationParams = {
	cursor?: string | null;
	limit?: number;
	search?: string;
};

// Read thread by ID (asc)
export async function getThreadById(
	threadId: string,
	params?: { limit?: number; cursor?: string },
) {
	return withAuthRetry((headers) =>
		apiClient.chat.thread[threadId]!.get({
			$headers: headers,
			$query: {
				...(params?.cursor ? { cursor: params.cursor } : {}),
				limit: params?.limit ?? 100,
			},
		}),
	);
}

export type ThreadById = Awaited<ReturnType<typeof getThreadById>>;

export async function getPublicThreadById(
	threadId: string,
	params?: { limit?: number; cursor?: string },
) {
	const result = await apiClient.public.chat.thread[threadId]!.get({
		$query: {
			...(params?.cursor ? { cursor: params.cursor } : {}),
			limit: params?.limit ?? 100,
		},
	});

	if (result.error) throw result.error;
	if (!result.data) throw new Error('No data returned');

	return result.data;
}

export type PublicThreadById = Awaited<ReturnType<typeof getPublicThreadById>>;

export function useThreadById(
	threadId: string | null,
	params?: { limit?: number },
	options?: { enabled?: boolean },
) {
	return useQuery<ThreadById>({
		queryKey: ['chat-thread', threadId, params],
		queryFn: () => {
			if (!threadId) throw new Error('threadId is required');
			return getThreadById(threadId, params);
		},
		enabled: !!threadId && (options?.enabled ?? true),
	});
}

export function usePublicThreadById(
	threadId: string | null,
	params?: { limit?: number },
	options?: { enabled?: boolean },
) {
	return useQuery<PublicThreadById>({
		queryKey: ['public-chat-thread', threadId, params],
		queryFn: () => {
			if (!threadId) throw new Error('threadId is required');
			return getPublicThreadById(threadId, params);
		},
		enabled: !!threadId && (options?.enabled ?? true),
	});
}

// Update thread by ID
export async function updateThreadById(
	threadId: string,
	data: { title: string },
) {
	return withAuthRetry((headers) =>
		apiClient.chat.thread[threadId]!.patch({
			$headers: headers,
			title: data.title,
		}),
	);
}

export function useUpdateThreadById() {
	const queryClient = useQueryClient();

	return async (threadId: string, data: { title: string }) => {
		await updateThreadById(threadId, data);

		await queryClient.invalidateQueries({
			queryKey: ['chat-thread', threadId],
		});
		await queryClient.invalidateQueries({
			queryKey: ['chat-threads'],
		});
	};
}

export async function updateThreadShare(
	threadId: string,
	data: { isPublic: boolean },
) {
	return withAuthRetry((headers) =>
		apiClient.chat.thread[threadId]!.share.patch({
			$headers: headers,
			isPublic: data.isPublic,
		}),
	);
}

export function useUpdateThreadShare() {
	const queryClient = useQueryClient();

	return async (threadId: string, data: { isPublic: boolean }) => {
		const res = await updateThreadShare(threadId, data);

		await queryClient.invalidateQueries({
			queryKey: ['chat-thread', threadId],
		});
		await queryClient.invalidateQueries({
			queryKey: ['chat-threads'],
		});

		return res;
	};
}

// Delete thread by ID
export async function deleteThreadById(threadId: string) {
	return withAuthRetry((headers) =>
		apiClient.chat.thread[threadId]!.delete({
			$headers: headers,
		}),
	);
}

export function useDeleteThreadById() {
	const queryClient = useQueryClient();

	return async (threadId: string) => {
		await deleteThreadById(threadId);

		await queryClient.invalidateQueries({
			queryKey: ['chat-threads'],
		});
	};
}

// Bulk delete threads by IDs
export async function deleteThreadsByIds(threadIds: string[]) {
	return withAuthRetry((headers) =>
		apiClient.chat.thread.delete({
			$headers: headers,
			ids: threadIds,
		}),
	);
}

export function useDeleteThreadsByIds() {
	const queryClient = useQueryClient();

	return async (threadIds: string[]) => {
		await deleteThreadsByIds(threadIds);

		await queryClient.invalidateQueries({
			queryKey: ['chat-threads'],
		});
	};
}

// List threads (desc)
export async function listThreads(params?: {
	cursor?: string | null;
	limit?: number;
	search?: string;
}) {
	return withAuthRetry((headers) =>
		apiClient.chat.thread.get({
			$headers: headers,
			$query: {
				...(params?.cursor ? { cursor: params.cursor } : {}),
				limit: params?.limit ?? 30,
				...(params?.search ? { search: params.search } : {}),
			},
		}),
	);
}

export type Threads = Awaited<ReturnType<typeof listThreads>>;

export type ThreadListItem = Threads[number];

function toCursorDate(value: unknown) {
	if (!value) return null;
	if (typeof value === 'string') return value;
	if (value instanceof Date) return value.toISOString();
	return null;
}

function getActivityCursor(thread: ThreadListItem) {
	return toCursorDate(
		(thread as { lastActivityAt?: Date | string | null }).lastActivityAt ??
			thread.createdAt,
	);
}

export function bumpThreadListItem(
	queryClient: QueryClient,
	threadId: string,
	lastActivityAt = new Date().toISOString(),
) {
	const activityDate = new Date(lastActivityAt);
	const queries = queryClient.getQueriesData<InfiniteData<Threads>>({
		queryKey: ['chat-threads'],
	});

	for (const [queryKey, data] of queries) {
		if (!data?.pages?.length) continue;

		let updated = false;
		const pages = data.pages.map((page) =>
			page.map((thread) => {
				if (thread.id !== threadId) return thread;
				updated = true;
				return {
					...thread,
					lastActivityAt: activityDate,
					lastMessageAt: activityDate,
				};
			}),
		);

		if (!updated) continue;

		queryClient.setQueryData<InfiniteData<Threads>>(queryKey, {
			...data,
			pages,
		});
	}
}

export function useThreads(params?: { limit?: number; search?: string }) {
	return useQuery<Threads>({
		queryKey: ['chat-threads', params],
		queryFn: () => listThreads(params),
	});
}

const THREADS_PAGE_SIZE = 30;

export function useInfiniteThreads(params?: { search?: string }) {
	return useInfiniteQuery({
		queryKey: ['chat-threads', { search: params?.search }],
		queryFn: ({ pageParam }) =>
			listThreads({
				search: params?.search,
				limit: THREADS_PAGE_SIZE,
				cursor: pageParam,
			}),
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) => {
			if (!lastPage || lastPage.length < THREADS_PAGE_SIZE) return undefined;
			const lastItem = lastPage[lastPage.length - 1];
			return lastItem ? getActivityCursor(lastItem) : undefined;
		},
	});
}

// Chat with LLM
export type ChatWithLLMInput = {
	prompt: string;
	extraReason?: boolean;
	threadId?: string;
	editedMessageId?: string;
	languagePreference?: string;
	files?: File[] | null;
};

export async function chatWithLLM(data: ChatWithLLMInput) {
	return withAuthRetry((headers) =>
		apiClient.chat.post({
			$headers: headers,
			...data,
		}),
	);
}

export type ChatWithLLMResponse = Awaited<ReturnType<typeof chatWithLLM>>;

export function useChatWithLLM() {
	const queryClient = useQueryClient();

	return async (data: ChatWithLLMInput) => {
		const res = await chatWithLLM(data);

		if (data.threadId) {
			await queryClient.invalidateQueries({
				queryKey: ['chat-thread', data.threadId],
			});
		}

		await queryClient.invalidateQueries({
			queryKey: ['chat-threads'],
		});

		return res;
	};
}

// Branch off message by ID
export async function branchOffMessage(messageId: string) {
	return withAuthRetry((headers) =>
		apiClient.chat['branch-off'][messageId]!.put({
			$headers: headers,
		}),
	);
}

export type BranchOffResponse = Awaited<ReturnType<typeof branchOffMessage>>;

export function useBranchOffMessage() {
	const queryClient = useQueryClient();

	return async (messageId: string) => {
		const res = await branchOffMessage(messageId);

		await queryClient.invalidateQueries({
			queryKey: ['chat-threads'],
		});

		return res;
	};
}
